import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchChatSessions } from '@/services/chatApi';
import { ChatSessionSummary } from '@/types/chat';

export interface ConversationRefreshTrigger {
  /** Fetch again when this goes from true to false. */
  active: boolean;
  /**
   * How long to wait first. The voice bot writes per turn and flushes on
   * exit, so its last rows land shortly after the transport disconnects.
   */
  delayMs?: number;
}

interface UseConversationSessionsOptions {
  /** False keeps whatever was last fetched and fetches nothing. */
  enabled?: boolean;
  refreshWhenFalse?: readonly ConversationRefreshTrigger[];
}

export interface UseConversationSessionsResult {
  /** The book's substantive conversations that have a line to sit on. */
  sessions: ChatSessionSummary[];
  refresh: () => void;
}

const NO_SESSIONS: ChatSessionSummary[] = [];

/**
 * A conversation that began with the player not ready has no line: voice
 * stores nulls, the text sheet sends '' and -1 and the store keeps them.
 */
export const isAnchoredSession = (session: ChatSessionSummary): boolean =>
  Boolean(session.anchor_chapter_id) && (
    (session.anchor_time_offset != null && session.anchor_time_offset >= 0)
    || (session.anchor_sentence_index != null && session.anchor_sentence_index >= 0)
  );

/**
 * The conversations a book's margin dots are placed from: one request per
 * book, then again when a trigger ends. The server's is_substantive filter
 * is the single rule for what counts as a conversation, so nothing is
 * inserted optimistically; a conversation appears on the next refresh.
 */
export const useConversationSessions = (
  bookId: string | null | undefined,
  { enabled = true, refreshWhenFalse = [] }: UseConversationSessionsOptions = {},
): UseConversationSessionsResult => {
  const [sessions, setSessions] = useState<ChatSessionSummary[]>(NO_SESSIONS);
  const requestRef = useRef(0);
  const timersRef = useRef<number[]>([]);
  const triggersRef = useRef(refreshWhenFalse);
  triggersRef.current = refreshWhenFalse;

  const refresh = useCallback(() => {
    if (!bookId || !enabled) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    fetchChatSessions(bookId, 'position')
      .then((response) => {
        if (requestId !== requestRef.current) return;
        const anchored = response.sessions.filter(isAnchoredSession);
        if (import.meta.env.DEV && anchored.length < response.sessions.length) {
          console.debug(
            `[margin dots] ${response.sessions.length - anchored.length} of ${response.sessions.length} conversations have no anchor and get no dot.`,
          );
        }
        setSessions(anchored);
      })
      .catch((error: unknown) => {
        if (import.meta.env.DEV) console.warn('[margin dots] Could not load conversations.', error);
      });
  }, [bookId, enabled]);

  // Another book's dots must never show over this one while its list loads.
  useEffect(() => {
    setSessions(NO_SESSIONS);
    requestRef.current += 1;
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, [bookId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeKey = refreshWhenFalse.map((trigger) => (trigger.active ? '1' : '0')).join('');
  const previousActiveKeyRef = useRef(activeKey);
  useEffect(() => {
    const previous = previousActiveKeyRef.current;
    previousActiveKeyRef.current = activeKey;
    triggersRef.current.forEach((trigger, index) => {
      if (previous[index] !== '1' || trigger.active) return;
      if (!trigger.delayMs) {
        refresh();
        return;
      }
      const id = window.setTimeout(() => {
        timersRef.current = timersRef.current.filter((pending) => pending !== id);
        refresh();
      }, trigger.delayMs);
      timersRef.current.push(id);
    });
  }, [activeKey, refresh]);

  useEffect(() => () => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  return { sessions, refresh };
};
